import type { CommunityRole, CurrentUserResponse, Membership } from "@/types/api";
import { EnvironmentService } from "@/utils/environment";

interface CommunityCapabilities {
    operate: boolean;
    install: boolean;
    manage: boolean;
}

const NONE: CommunityCapabilities = { operate: false, install: false, manage: false };

// What each community role unlocks in the web portal. Supervisor only affects
// the mobile app; FirstResponder has no portal access until its scope is decided.
const COMMUNITY_CAPABILITIES: Record<CommunityRole, CommunityCapabilities> = {
    Owner: { operate: true, install: true, manage: true },
    Admin: { operate: true, install: true, manage: true },
    Operator: { operate: true, install: false, manage: false },
    Security: { operate: true, install: false, manage: false },
    Installer: { operate: false, install: true, manage: false },
    FirstResponder: NONE,
    Supervisor: NONE,
    Member: NONE,
};

const capabilitiesOf = (role: string | null | undefined): CommunityCapabilities =>
    (role && COMMUNITY_CAPABILITIES[role as CommunityRole]) || NONE;

/**
 * Platform Admins can manage every community, but Operate/Install always come
 * from the user's own community role.
 */
export class UserPermissionsService {
    static isPlatformAdmin(user: CurrentUserResponse): boolean {
        return user.role === "Admin";
    }

    static getMembership(user: CurrentUserResponse, communityId: number): Membership | null {
        return user.memberships?.find((m) => m.community?.id === communityId) ?? null;
    }

    /** The user's real role in the community, or null when not a member. */
    static getCommunityRole(user: CurrentUserResponse, communityId: number): CommunityRole | null {
        return this.getMembership(user, communityId)?.communityRole ?? null;
    }

    static canOperate(user: CurrentUserResponse, communityId: number): boolean {
        return capabilitiesOf(this.getCommunityRole(user, communityId)).operate;
    }

    static canInstall(user: CurrentUserResponse, communityId: number): boolean {
        return capabilitiesOf(this.getCommunityRole(user, communityId)).install;
    }

    /** Members, venues, groups, Skyward, policy and sync. */
    static canManage(user: CurrentUserResponse, communityId: number): boolean {
        return (
            this.isPlatformAdmin(user) ||
            capabilitiesOf(this.getCommunityRole(user, communityId)).manage
        );
    }

    static canTransferOwnership(user: CurrentUserResponse, communityId: number): boolean {
        return this.isPlatformAdmin(user) || this.getCommunityRole(user, communityId) === "Owner";
    }

    static canCreateCommunity(user: CurrentUserResponse): boolean {
        return this.isPlatformAdmin(user);
    }

    static canEditCommunity(user: CurrentUserResponse): boolean {
        return this.isPlatformAdmin(user);
    }

    static canDeleteCommunity(user: CurrentUserResponse): boolean {
        return this.isPlatformAdmin(user);
    }

    /** Platform user management (/users). */
    static canManageUsers(user: CurrentUserResponse): boolean {
        return this.isPlatformAdmin(user);
    }

    /**
     * Whether the user gets the Communities page instead of the welcome page.
     * Platform Installers get it even before they're added to a community.
     */
    static hasPortalAccess(user: CurrentUserResponse): boolean {
        if (this.isPlatformAdmin(user) || user.role === "Installer") return true;
        return (
            user.memberships?.some((m) => {
                const caps = capabilitiesOf(m.communityRole);
                return caps.operate || caps.install || caps.manage;
            }) ?? false
        );
    }

    static getMembershipByOrgName(user: CurrentUserResponse, orgName: string) {
        return user.memberships?.find(m => m.community.orgName === orgName) ?? null;
    }

    /**
     * Get user's membership for the current community (from subdomain)
     */
    static getCurrentCommunityMembership(user: CurrentUserResponse) {
        const orgName = EnvironmentService.getCommunity();
        if (!orgName) return null;
        return this.getMembershipByOrgName(user, orgName);
    }

    /**
     * Get default venue ID for user's current community
     */
    static getDefaultVenueId(user: CurrentUserResponse): number | null {
        const membership = this.getCurrentCommunityMembership(user);
        const venues = membership?.community?.venues;
        if (!venues?.length) return null;
        const defaultVenue = venues.find(v => v.isDefault) || venues[0];
        return defaultVenue?.id ?? null;
    }
}
